/**
 * Builds the schema.org @graph for a Product listing page (`/{locale}/products/{id}`), which
 * lists every seller's own Offer of that Product, each at its own price - unlike the Roblox
 * landing (`robuxPageJsonLd.js`), a single first-party seller with one price, this is a genuine
 * multi-merchant listing, so `Product.offers` is an AggregateOffer with a real lowPrice/highPrice
 * spanning the offers actually on the page, plus the individual Offers nested inside it
 * (`AggregateOffer.offers`) - schema.org's own documented way to list the constituent offers of
 * an aggregate, and something neither of this project's two existing JSON-LD builders do.
 *
 * Deliberately emits **no** AggregateRating: this marketplace's reviews are tied to sellers
 * (Team.rating/reviews_count - see app/Models/Team.php), not to a specific Product, and
 * Team.rating is hardcoded to a fabricated 4.0 whenever a seller has fewer than 5 real reviews
 * (Team::updateReviewsCountAndRating()). Aggregating those per-seller scores into a single
 * Product-level rating would both misattribute a seller's general trustworthiness as if it were
 * feedback on this one product, and - for any seller under that 5-review floor - publish a
 * number that was never a real rating in the first place. Same reasoning as
 * robuxPageJsonLd.js's own AggregateRating omission, applied for a different underlying reason.
 *
 * SSR-safe: pure string/number work, no DOM access.
 *
 * @param {Object}   cfg
 * @param {string}   cfg.url               Canonical URL of the page.
 * @param {string}   cfg.name              Product title.
 * @param {string}  [cfg.description]      Product description.
 * @param {string}  [cfg.image]            Absolute image URL.
 * @param {string}   cfg.currency          ISO currency of the displayed prices (e.g. "RUB"/"USD") -
 *                                         must match what's visibly shown, since prices are
 *                                         already in the viewer's resolved display currency
 *                                         (Price::getCurrency()), not a fixed base currency.
 * @param {Array<{price: number, sellerName?: string, url?: string}>} cfg.offers
 *                                         One entry per seller Offer currently listed on the page.
 * @param {Array<{name: string, url: string}>} [cfg.breadcrumbs] Ordered root→current.
 * @returns {Object} JSON-LD object with @context and @graph.
 */
export function buildProductPageGraph(cfg = {}) {
    const {url, name, description, image, currency, offers, breadcrumbs} = cfg;

    const product = {
        '@type': 'Product',
        '@id': `${url}#product`,
        name,
    };
    if (description) product.description = description;
    if (image) product.image = image;

    const validOffers = (Array.isArray(offers) ? offers : [])
        .filter((offer) => Number.isFinite(offer?.price) && offer.price > 0);

    if (validOffers.length > 0 && currency) {
        const prices = validOffers.map((offer) => offer.price);
        const lowPrice = round2(Math.min(...prices));
        const highPrice = round2(Math.max(...prices));

        product.offers = {
            '@type': 'AggregateOffer',
            url,
            priceCurrency: currency,
            lowPrice,
            offerCount: validOffers.length,
            availability: 'https://schema.org/InStock',
            offers: validOffers.map((offer) => ({
                '@type': 'Offer',
                price: round2(offer.price),
                priceCurrency: currency,
                availability: 'https://schema.org/InStock',
                ...(offer.url ? {url: offer.url} : {}),
                ...(offer.sellerName ? {seller: {'@type': 'Person', name: offer.sellerName}} : {}),
            })),
        };
        if (highPrice !== lowPrice) {
            product.offers.highPrice = highPrice;
        }
    }

    const graph = [product];

    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
        graph.push({
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((crumb, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: crumb.name,
                item: crumb.url,
            })),
        });
    }

    return {'@context': 'https://schema.org', '@graph': graph};
}

/**
 * A formatted price string (e.g. "1 234,56 ₽" or "$1,234.56") back to a plain number, matching
 * the convention already used by Offer/Show.vue and ProductPage/Show.vue's own JSON-LD - the
 * formatted string, not the raw stored minor-units value, because it's already in the viewer's
 * resolved display currency and Google requires structured-data prices to match what's shown.
 *
 * Unlike those two call sites' inline `.replace(',', '.')`, this also strips thousands
 * separators rather than just swapping the decimal mark: CurrencyAmount::toUI() groups RU
 * amounts with a space ("1 234,56 ₽") but EN ones with a comma ("$1,234.56"), and a bare
 * comma-to-period swap turns the EN case into "1.234.56" (parseFloat stops at the second period,
 * silently truncating to 1.234) the moment an amount reaches four figures. The last comma/period
 * in the string is always the real decimal mark here, since CurrencyAmount::toUI() always emits
 * exactly two fractional digits - so slice on that, discard every other separator as grouping.
 *
 * @param {string|null|undefined} formatted
 * @returns {number|null}
 */
export function parseFormattedPrice(formatted) {
    if (typeof formatted !== 'string') return null;
    const digitsAndSeparators = formatted.replace(/[^\d.,]/g, '');
    if (!digitsAndSeparators) return null;

    const decimalIndex = Math.max(digitsAndSeparators.lastIndexOf(','), digitsAndSeparators.lastIndexOf('.'));
    const normalised =
        decimalIndex === -1
            ? digitsAndSeparators
            : `${digitsAndSeparators.slice(0, decimalIndex).replace(/[.,]/g, '')}.${digitsAndSeparators.slice(decimalIndex + 1).replace(/[.,]/g, '')}`;

    const parsed = parseFloat(normalised);
    return Number.isFinite(parsed) ? parsed : null;
}

function round2(value) {
    return Math.round(value * 100) / 100;
}
